import React from 'react';
import PropTypes from 'prop-types';
import { formatPartNumber } from '@sainsburys-tech/boltui-utils';
import { Button, Image, Ratings, AddToWishlist } from '@sainsburys-tech/bolt';
import ProductPanelSkeleton from './ProductPanel.Skeleton';
import * as Styled from './ProductPanel.styles';

var ProductPanel = function ProductPanel(_ref) {
  var partNumber = _ref.partNumber,
      name = _ref.name,
      price = _ref.price,
      wasPrice = _ref.wasPrice,
      flashText = _ref.flashText,
      showPriceLastLine = _ref.showPriceLastLine,
      showWishlistButton = _ref.showWishlistButton,
      linkTitle = _ref.linkTitle,
      rating = _ref.rating,
      ratingCount = _ref.ratingCount,
      quantity = _ref.quantity,
      imageWidth = _ref.imageWidth,
      imageHeight = _ref.imageHeight,
      showBorder = _ref.showBorder,
      condensed = _ref.condensed,
      lazy = _ref.lazy,
      showThumbnail = _ref.showThumbnail,
      showPartNumber = _ref.showPartNumber,
      moreDetailsButton = _ref.moreDetailsButton,
      moreDetailsButtonText = _ref.moreDetailsButtonText,
      moreDetailsButtonOnClick = _ref.moreDetailsButtonOnClick,
      onClick = _ref.onClick,
      children = _ref.children,
      variantAttributes = _ref.variantAttributes,
      dataTest = _ref['data-test'];
  var showRightSideDetails = !showPriceLastLine && !showWishlistButton && (price || flashText || wasPrice || moreDetailsButton);
  var showVariantAttributes = variantAttributes && Object.keys(variantAttributes).some(function (key) {
    return ['colour', 'fabricType', 'legColour'].includes(key);
  });
  return /*#__PURE__*/React.createElement(Styled.Wrapper, {
    onClick: moreDetailsButtonOnClick ? null : function () {
      return onClick(partNumber);
    },
    "data-test": dataTest
  }, /*#__PURE__*/React.createElement(Styled.Container, {
    showBorder: showBorder,
    showWishlistButton: showWishlistButton
  }, /*#__PURE__*/React.createElement(Styled.DetailsContainer, {
    onClick: moreDetailsButtonOnClick ? function () {
      return onClick(partNumber);
    } : null,
    "data-test": "".concat(dataTest, "-details")
  }, showThumbnail && /*#__PURE__*/React.createElement(Styled.Thumbnail, {
    condensed: condensed
  }, /*#__PURE__*/React.createElement(Image, {
    src: "//media.4rgos.it/s/Argos/".concat(partNumber, "_R_SET"),
    alt: name,
    width: imageWidth,
    height: imageHeight,
    lazy: lazy,
    "data-test": "".concat(dataTest, "-image")
  })), /*#__PURE__*/React.createElement(Styled.Details, null, /*#__PURE__*/React.createElement(Styled.Name, {
    as: linkTitle ? 'a' : 'div',
    href: linkTitle ? "/product/".concat(partNumber) : null,
    "data-test": "".concat(dataTest, "-name")
  }, name), !!rating && /*#__PURE__*/React.createElement(Styled.Ratings, null, /*#__PURE__*/React.createElement(Ratings, {
    rating: rating,
    reviewsCount: ratingCount,
    position: "left",
    "data-test": "".concat(dataTest, "-ratings")
  })), showVariantAttributes && /*#__PURE__*/React.createElement(Styled.VariantAttributes, {
    "data-test": "".concat(dataTest, "-variant-attributes")
  }, /*#__PURE__*/React.createElement("ul", {
    "aria-label": "Attributes"
  }, (variantAttributes.colour || variantAttributes.fabricType) && /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("strong", null, "Fabric:"), " ", variantAttributes.colour, " ", variantAttributes.fabricType), variantAttributes.legColour && /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("strong", null, "Legs:"), " ", variantAttributes.legColour))), showPartNumber && /*#__PURE__*/React.createElement(Styled.PartNumber, {
    condensed: condensed,
    "data-test": "".concat(dataTest, "-part-number")
  }, formatPartNumber(partNumber)), /*#__PURE__*/React.createElement(Styled.LastRow, null, quantity && /*#__PURE__*/React.createElement(Styled.Quantity, {
    "data-test": "".concat(dataTest, "-quantity")
  }, "Qty: ", quantity), showPriceLastLine && price && /*#__PURE__*/React.createElement(Styled.InlinePrice, {
    price: price,
    "data-test": "".concat(dataTest, "-price")
  }, "\xA3", price.toFixed(2))), children && /*#__PURE__*/React.createElement(Styled.Custom, null, children))), showRightSideDetails && /*#__PURE__*/React.createElement(Styled.Prices, {
    showThumbnail: showThumbnail,
    "data-test": "".concat(dataTest, "-prices")
  }, price && /*#__PURE__*/React.createElement(Styled.Price, {
    price: price,
    "data-test": "".concat(dataTest, "-price")
  }, "\xA3", price.toFixed(2)), flashText && /*#__PURE__*/React.createElement(Styled.Price, {
    flashText: flashText,
    "data-test": "".concat(dataTest, "-flash-text")
  }, flashText), wasPrice && /*#__PURE__*/React.createElement(Styled.Price, {
    wasPrice: wasPrice,
    "data-test": "".concat(dataTest, "-was-price")
  }, "Was \xA3", wasPrice.toFixed(2)), moreDetailsButton && /*#__PURE__*/React.createElement(Styled.MoreDetails, null, /*#__PURE__*/React.createElement(Button, {
    small: true,
    kind: "secondary",
    onClick: moreDetailsButtonOnClick ? function () {
      return moreDetailsButtonOnClick(partNumber);
    } : null,
    "data-test": "".concat(dataTest, "-button-more-details")
  }, moreDetailsButtonText))), showWishlistButton && /*#__PURE__*/React.createElement(Styled.AddToWishlistButtonContainer, null, /*#__PURE__*/React.createElement(AddToWishlist.Button, {
    item: {
      partNumber: partNumber
    },
    tooltipPosition: "right"
  }))));
};

ProductPanel.displayName = 'ProductPanel';
ProductPanel.propTypes = {
  /**
   * Required prop for the part number of the item to display
   */
  partNumber: PropTypes.string.isRequired,

  /**
   * Required prop for the name of the item to display
   */
  name: PropTypes.string.isRequired,

  /**
   * Optional prop for the price of the item to display
   */
  price: PropTypes.number,

  /**
   * Optional prop for the saving if on sale
   */
  flashText: PropTypes.string,

  /**
   * Optional prop for "was" price
   */
  wasPrice: PropTypes.number,

  /**
   * Optional prop to show rhe rating of the product
   */
  rating: PropTypes.number,

  /**
   * Optional prop for the rating count of the product
   */
  ratingCount: PropTypes.number,

  /**
   * Optional prop for the quantity of the product
   */
  quantity: PropTypes.number,

  /**
   * Optional prop to set the width of the image
   */
  imageWidth: PropTypes.number,

  /**
   * Optional prop to set the height of the image
   */
  imageHeight: PropTypes.number,

  /**
   * Optional prop to show a border
   */
  showBorder: PropTypes.bool,

  /**
   * Optional prop to show a thumbnail with image
   */
  showThumbnail: PropTypes.bool,

  /**
   * Optional prop to show the part number
   */
  showPartNumber: PropTypes.bool,

  /**
   * Optional prop to show a add to to wishlist button
   */
  showWishlistButton: PropTypes.bool,

  /**
   * Optional prop to show the more details button (EG inside UGC/Social Content)
   */
  moreDetailsButton: PropTypes.bool,

  /**
   * Optional prop to update text on more details button
   */
  moreDetailsButtonText: PropTypes.string,

  /**
   * Optional onclick to fire if moreDetailsButton is clicked. (if onClick is provided and no moreDetailsButtonOnClick. onClick will be trigger)
   */
  moreDetailsButtonOnClick: PropTypes.func,

  /**
   * Optional prop if the price should be displayed on the last line
   */
  showPriceLastLine: PropTypes.bool,

  /**
   * Optional prop if the layout of the items within the card should use a condensed layout (for if the card is displaying where there is limited space)
   */
  condensed: PropTypes.bool,

  /**
   * Optional prop to lazy load the thumbnail image
   */
  lazy: PropTypes.bool,

  /**
   * Optional prop if the title should link to the product page (default true)
   */
  linkTitle: PropTypes.bool,

  /**
   * Optional onclick to fire if the component or moreDetailsButton is clicked
   */
  onClick: PropTypes.func,

  /**
   * Specify an optional data-test attribute to be added to your Image for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Optional prop if the product has Made To Order attributes
   */
  variantAttributes: PropTypes.shape({
    colour: PropTypes.string,
    fabricType: PropTypes.string,
    legColour: PropTypes.string
  })
};
ProductPanel.defaultProps = {
  showBorder: true,
  showThumbnail: true,
  showPartNumber: false,
  showPriceLastLine: false,
  showWishlistButton: false,
  imageWidth: 70,
  imageHeight: 70,
  condensed: false,
  lazy: true,
  linkTitle: true,
  moreDetailsButton: false,
  moreDetailsButtonText: 'More Details',
  moreDetailsButtonOnClick: null,
  onClick:
  /* istanbul ignore next */
  function onClick() {},
  'data-test': 'component-product-panel'
};
ProductPanel.ProductPanelSkeleton = ProductPanelSkeleton;
export default ProductPanel;
export { ProductPanelSkeleton };